import { useState } from 'react';
import { Mail, ExternalLink, Send, CheckCircle, Youtube, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const TEAL_LOGO = "https://customer-assets.emergentagent.com/job_citizen-refresh/artifacts/2gf9jka6_customcolor_logo_transparent_background.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState('idle');

  const handleNewsletter = async (e) => {
    e.preventDefault();
    setSubStatus('loading');
    try {
      await axios.post(`${API}/newsletter`, { email });
      setSubStatus('success');
      setEmail('');
    } catch {
      setSubStatus('success'); // Still show success for UX
    }
  };

  return (
    <footer data-testid="footer" className="relative bg-[#080808] border-t border-white/5">
      {/* Newsletter bar */}
      <div className="border-b border-white/5 py-10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Stay Informed
              </h3>
              <p className="text-gray-500 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Perspectives on jurisdictional strategy, delivered periodically.
              </p>
            </div>
            {subStatus === 'success' ? (
              <div data-testid="newsletter-success" className="flex items-center gap-2 text-[#1a7a7a] text-sm font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <CheckCircle size={16} /> You're subscribed!
              </div>
            ) : (
              <form onSubmit={handleNewsletter} data-testid="newsletter-form" className="flex gap-3 w-full md:w-auto">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="newsletter-email-input"
                  placeholder="your@email.com"
                  className="bg-black/40 border border-white/10 focus:border-[#1a7a7a] text-white placeholder:text-white/25 rounded-lg px-4 py-2.5 text-sm transition-all outline-none focus:ring-1 focus:ring-[#1a7a7a]/50 w-full md:w-64"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  className="bg-[#1a7a7a] text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#2d9e9e] transition-all text-sm flex items-center gap-2 whitespace-nowrap"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  <Send size={14} /> Subscribe
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="py-14">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div>
              <img src={TEAL_LOGO} alt="Global Citizen Consulting" className="h-10 w-auto mb-4" />
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Independent strategic advisory. Jurisdictional strategy for lives that cross borders.
              </p>
              <a
                href="https://calendly.com/chr-globalcitizen/video-consultation-clone"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-consultation-link"
                className="inline-flex items-center gap-2 mt-4 text-[#1a7a7a] text-sm font-medium hover:underline"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Request an Introduction
              </a>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-[#1a7a7a] text-sm uppercase tracking-widest mb-5 font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Quick Links
              </h4>
              <ul className="space-y-2.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {[
                  { label: 'The Firm', href: '#about' },
                  { label: 'Our Founders', href: '#our-story' },
                  { label: 'Testimonials', href: '#testimonials' },
                  { label: 'FAQ', href: '/faq', isRoute: true },
                  { label: 'Contact', href: '#contact' },
                ].map((link) => (
                  <li key={link.label}>
                    {link.isRoute ? (
                      <Link
                        to={link.href}
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/[\s&]/g, '-')}`}
                        className="text-gray-400 hover:text-[#1a7a7a] transition-colors text-sm"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/[\s&]/g, '-')}`}
                        className="text-gray-400 hover:text-[#1a7a7a] transition-colors text-sm"
                        onClick={(e) => {
                          e.preventDefault();
                          document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
                <li>
                  <Link
                    to="/blog"
                    data-testid="footer-link-blog"
                    className="text-gray-400 hover:text-[#1a7a7a] transition-colors text-sm"
                  >
                    Blog & Resources
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-[#1a7a7a] text-sm uppercase tracking-widest mb-5 font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Resources
              </h4>
              <ul className="space-y-2.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {[
                  { label: 'The Global Freedom Blueprint', href: 'https://amzn.to/49PZdvQ' },
                  { label: 'No Borders on Our Dreams', href: 'https://open.spotify.com/album/6b7qImvJf6PgzZ7fDKgoej?si=Ho-5sjteT8-7FsZzwVfguA' },
                  { label: 'Global Citizen App', href: 'https://apps.apple.com/us/app/global-citizen-app/id6759657870' },
                ].map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target={link.href.startsWith('#') ? undefined : '_blank'}
                      rel={link.href.startsWith('#') ? undefined : 'noopener noreferrer'}
                      data-testid={`footer-resource-${link.label.toLowerCase().replace(/[\s:]/g, '-')}`}
                      className="text-gray-400 hover:text-[#1a7a7a] transition-colors text-sm flex items-center gap-1"
                      onClick={(e) => {
                        if (link.href.startsWith('#')) {
                          e.preventDefault();
                          document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    >
                      {link.label}
                      {!link.href.startsWith('#') && <ExternalLink size={10} />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Group + Legal */}
            <div>
              <h4 className="text-[#1a7a7a] text-sm uppercase tracking-widest mb-5 font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Our Group
              </h4>
              <ul className="space-y-2.5 mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <li>
                  <a
                    href="https://www.gcc-offshore.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-link-gcc-offshore"
                    className="text-gray-400 hover:text-[#d4af37] transition-colors text-sm flex items-center gap-1"
                  >
                    GCC Offshore &mdash; Offshore Services
                    <ExternalLink size={10} />
                  </a>
                </li>
                <li>
                  <a
                    href="https://familyoffice.globalcitizenconsult.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-link-family-office"
                    className="text-gray-400 hover:text-[#1a7a7a] transition-colors text-sm flex items-center gap-1"
                  >
                    Family Office Practice
                    <ExternalLink size={10} />
                  </a>
                </li>
              </ul>
              <h4 className="text-[#1a7a7a] text-sm uppercase tracking-widest mb-5 font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Legal
              </h4>
              <ul className="space-y-2.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <li>
                  <Link
                    to="/privacy"
                    data-testid="footer-link-privacy"
                    className="text-gray-400 hover:text-[#1a7a7a] transition-colors text-sm"
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/6 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              &copy; {new Date().getFullYear()} Global Citizen Consulting. All rights reserved.
            </p>
            <div className="flex items-center gap-5">
              <a
                href="https://www.youtube.com/@GlobalCitizenConsulting"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-youtube"
                className="text-gray-500 hover:text-[#FF0000] transition-colors"
                aria-label="YouTube"
              >
                <Youtube size={18} />
              </a>
              <a
                href="https://www.instagram.com/GlobalCitizenConsulting"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="text-gray-500 hover:text-[#E1306C] transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href="mailto:contact@globalcitizenconsult.com"
                data-testid="footer-email-link"
                className="text-gray-500 hover:text-[#1a7a7a] transition-colors text-xs flex items-center gap-2"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <Mail size={14} />
                contact@globalcitizenconsult.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
